import axios from 'axios';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faEye,
  faEyeSlash,
  faBuilding,
  faCircleCheck,
  faArrowLeft,
  faShieldHalved,
} from '@fortawesome/free-solid-svg-icons';
import { customerAuthService } from '@/services/customer-auth.service';
import { CUSTOMER_PORTAL_ROUTES } from '@/routes/customer-portal-routes';
import { RecoverySessionCounter } from '@/components/shared/RecoverySessionCounter';

/**
 * B6.14 final UI closure — Customer Portal activation.
 *
 * The one thing missing after B6.14's backend work: an invited customer could be
 * enabled and emailed a 6-digit code, but had nowhere to type it. This page is that
 * place. It is also the forgotten-password path, because the backend makes no
 * distinction between the two — `resetPassword()` has always accepted a contact with
 * no existing password, which is exactly why the invitation could reuse the OTP flow
 * instead of needing a token system of its own.
 *
 * No new endpoint, no new authentication, no new invitation mechanism. Three existing
 * stages:
 *
 *   password/request     → emails a 6-digit OTP
 *   password/verify-otp  → exchanges the OTP for a short-lived reset token
 *   password/reset       → sets the password, bumps tokenVersion, returns 204
 *
 * ── What is deliberately NOT persisted ──────────────────────────────────────────
 * The OTP, the reset token and both password fields live in component state and
 * nowhere else. Nothing is written to localStorage or sessionStorage, nothing is put
 * in the URL, and nothing is logged. Unmounting the page destroys all of it — which is
 * the intended behaviour, since the reset token is short-lived by design.
 *
 * The route carries at most a NON-SECRET `?email=` hint so the invitation link can
 * pre-fill the address; possession of that address grants nothing on its own, and the
 * OTP still has to arrive in the customer's mailbox.
 */

type Step = 'request' | 'verify' | 'password' | 'done';

/**
 * Mirrors CustomerPasswordResetService.validatePasswordPolicy exactly. Kept in step
 * deliberately: a frontend rule that is weaker lets the user submit something the
 * server will reject, and one that is stricter rejects passwords the server would
 * accept. The server remains the authority — this only avoids a pointless round trip,
 * and server-reported violations are always displayed verbatim.
 */
const PASSWORD_RULES: { label: string; test: (v: string) => boolean }[] = [
  { label: 'At least 8 characters', test: (v) => v.length >= 8 },
  { label: 'One uppercase letter', test: (v) => /[A-Z]/.test(v) },
  { label: 'One lowercase letter', test: (v) => /[a-z]/.test(v) },
  { label: 'One number', test: (v) => /[0-9]/.test(v) },
  { label: 'One special character', test: (v) => /[^A-Za-z0-9]/.test(v) },
];

interface ApiErrorBody {
  error?: { code?: string; message?: string; violations?: string[]; retryAfterSeconds?: number };
}

function errorBody(err: unknown): ApiErrorBody['error'] | undefined {
  return axios.isAxiosError(err)
    ? (err.response?.data as ApiErrorBody | undefined)?.error
    : undefined;
}

/**
 * Turns the backend's error codes into customer-readable text. The codes are the
 * contract — they are matched here rather than the messages, which are free to change.
 * Anything unrecognised falls back to the server's own message before a generic one, so
 * a new backend code degrades to something useful rather than to "Something went wrong".
 */
function describeError(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err) && !err.response) {
    return 'Unable to reach the server. Check your connection and try again.';
  }
  const body = errorBody(err);
  switch (body?.code) {
    case 'OTP_INVALID':
      return 'That code is not correct. Please check the email and try again.';
    case 'OTP_NOT_FOUND':
      return 'That code has expired or has already been used. Request a new one below.';
    case 'OTP_LOCKED':
      return 'Too many incorrect attempts. This code is now locked — request a new one.';
    case 'RESET_TOKEN_INVALID':
      return 'This activation session is no longer valid. Please start again.';
    case 'PASSWORD_POLICY_VIOLATION':
      return body.violations?.length
        ? body.violations.join('. ')
        : 'That password does not meet the requirements.';
    case 'RESEND_COOLDOWN':
      return `Please wait ${body.retryAfterSeconds ?? 60} seconds before requesting another code.`;
    case 'DAILY_LIMIT_EXCEEDED':
      return 'You have requested too many codes today. Please try again tomorrow, or contact your service provider.';
    case 'EMAIL_DELIVERY_FAILED':
      return 'We could not send the email just now. Please try again shortly.';
    default:
      return body?.message ?? fallback;
  }
}

export function CustomerActivationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState<Step>('request');
  const [email, setEmail] = useState(searchParams.get('email')?.trim() ?? '');
  /** D-139 — wording only. Never gates an endpoint, a limit or a security control. */
  const isReset = searchParams.get('mode') === 'reset';
  const [otp, setOtp] = useState('');
  // In memory only, for the lifetime of this component. Never persisted.
  const [resetToken, setResetToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [maskedEmail, setMaskedEmail] = useState('');
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [cooldown, setCooldown] = useState(0);
  /**
   * D-143 — requests made from THIS browser, counted server-side before the address
   * was looked up, so it reads identically for a real contact and an unknown
   * address. Never the account's OTP count: that number would differ between those
   * two cases and so may only be disclosed inside the OTP email.
   */
  const [sessionRequestsUsed, setSessionRequestsUsed] = useState(0);
  const [sessionRequestLimit, setSessionRequestLimit] = useState(5);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Resend cooldown ticker. Cleared on unmount so no timer outlives the page.
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setInterval(() => setCooldown((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => window.clearInterval(id);
  }, [cooldown]);

  const reset = () => {
    setFieldErrors({});
    setFormError(null);
  };

  // ── Stage 1 — request the OTP ─────────────────────────────────────────────
  const submitRequest = async (e: FormEvent) => {
    e.preventDefault();
    reset();
    if (!email.trim()) {
      setFieldErrors({
        email: isReset
          ? 'Enter your registered email address'
          : 'Enter the email address your invitation was sent to',
      });
      return;
    }
    setLoading(true);
    try {
      const res = await customerAuthService.requestPasswordOtp(email.trim());
      setMaskedEmail(res.maskedEmail);
      setSessionRequestsUsed(res.sessionRequestsUsed);
      setSessionRequestLimit(res.sessionRequestLimit);
      setExpiresAt(new Date(res.otpExpiresAt));
      setCooldown(res.cooldownSeconds);
      setNotice(null);
      setStep('verify');
    } catch (err) {
      setFormError(describeError(err, 'Could not send the code. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  // ── Stage 2 — verify the OTP ──────────────────────────────────────────────
  const submitVerify = async (e: FormEvent) => {
    e.preventDefault();
    reset();
    if (!/^\d{6}$/.test(otp)) {
      setFieldErrors({ otp: 'Enter the 6-digit code from your email' });
      return;
    }
    setLoading(true);
    try {
      const res = await customerAuthService.verifyPasswordOtp(email.trim(), otp);
      setResetToken(res.resetToken);
      setNotice(null);
      setStep('password');
    } catch (err) {
      setFormError(describeError(err, 'Could not verify that code. Please try again.'));
      setOtp('');
    } finally {
      setLoading(false);
    }
  };

  // ── Resend ────────────────────────────────────────────────────────────────
  const resend = async () => {
    reset();
    setLoading(true);
    try {
      const res = await customerAuthService.requestPasswordOtp(email.trim());
      setExpiresAt(new Date(res.otpExpiresAt));
      setCooldown(res.cooldownSeconds);
      setSessionRequestsUsed(res.sessionRequestsUsed);
      setSessionRequestLimit(res.sessionRequestLimit);
      setOtp('');
      // D-142 rule 1, applied to the RESEND path too — it asserted delivery ("A new
      // code has been sent to …") on a request the server may have suppressed for
      // the daily cap, the cooldown or a send failure. The server's answer is the
      // same neutral 200 in every one of those cases, so the notice states what is
      // actually true and the counter beside it carries the rest.
      setNotice(`If an account exists for ${res.maskedEmail}, another code is on its way.`);
    } catch (err) {
      setFormError(describeError(err, 'Could not resend the code. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  // ── Stage 3 — set the password ────────────────────────────────────────────
  const submitPassword = async (e: FormEvent) => {
    e.preventDefault();
    reset();
    const errs: Record<string, string> = {};
    const unmet = PASSWORD_RULES.filter((r) => !r.test(password));
    if (unmet.length > 0) errs.password = 'Your password does not meet all the requirements yet';
    if (password !== confirm) errs.confirm = 'Both passwords must match';
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }
    setLoading(true);
    try {
      await customerAuthService.resetPassword(resetToken, password, confirm);
      // Clear the secrets from memory the moment they are no longer needed.
      setPassword('');
      setConfirm('');
      setResetToken('');
      setOtp('');
      setStep('done');
    } catch (err) {
      const body = errorBody(err);
      setFormError(describeError(err, 'Could not set your password. Please try again.'));
      // An invalid reset token means the session is unrecoverable — send the
      // customer back to the start rather than leaving them on a dead form.
      if (body?.code === 'RESET_TOKEN_INVALID') {
        setStep('request');
        setResetToken('');
      }
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'flex h-11 w-full rounded-lg border border-customer-border bg-customer-surface px-3 py-2 text-sm text-customer-text placeholder:text-customer-text-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring focus:border-customer-primary disabled:cursor-not-allowed disabled:opacity-50';
  const buttonClass =
    'customer-card-interactive mt-1 flex min-h-11 w-full items-center justify-center rounded-lg bg-customer-primary px-4 py-2 text-sm font-medium text-customer-action-ink transition-colors hover:bg-customer-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring focus-visible:ring-offset-2 focus-visible:ring-offset-customer-surface disabled:cursor-not-allowed disabled:opacity-60';

  // D-139 — the same three-stage OTP flow serves invitation activation and password
  // recovery; the backend has always treated them as one operation (prove control of
  // the address, then set a password). `?mode=reset` therefore changes NOTHING but the
  // wording: same endpoints, same OTP, same limits, same reset token, same completion.
  // Anything other than `reset` — including no parameter at all — keeps the invitation
  // wording, so the activation path reached from an emailed link is untouched.

  // D-142 rules 1-3 - the verify copy must not claim an email was sent. The server
  // returns the SAME neutral response for a real account, an unknown address, the
  // daily cap, the cooldown and a delivery failure (D-141 rules 2-4), so the previous
  // "We sent a 6-digit code" over-promised on every suppressed request. The limits
  // stated here are POLICY - constant for every caller - and so disclose nothing about
  // any account. One string, shared by the reset and activation variants, because the
  // security meaning must be identical for every caller (rule 3).
  // D-143 — the policy tail moved into <RecoverySessionCounter/>, which carries the
  // same safety content plus the browser-scoped count. The conditional opening
  // clause stays exactly as D-142 wrote it: it still must not assert delivery.
  const verifyBlurb = maskedEmail
    ? `If an account exists for ${maskedEmail}, a 6-digit code is on its way.`
    : 'If an account exists, a 6-digit code is on its way.';

  const heading: Record<Step, { title: string; blurb: string }> = isReset
    ? {
        request: {
          title: 'Reset your password',
          blurb: 'Enter your registered email address and we will send you a code.',
        },
        verify: {
          title: 'Enter your code',
          blurb: verifyBlurb,
        },
        password: { title: 'Choose a new password', blurb: 'Set a new password for your account.' },
        done: { title: 'Password reset', blurb: 'Your password has been changed.' },
      }
    : {
        request: {
          title: 'Activate your account',
          blurb: 'Enter the email address your invitation was sent to and we will send you a code.',
        },
        verify: {
          title: 'Enter your code',
          blurb: verifyBlurb,
        },
        password: { title: 'Create your password', blurb: 'Choose a password for your account.' },
        done: { title: 'Account activated', blurb: 'Your password has been set.' },
      };

  return (
    <div className="flex min-h-screen items-center justify-center bg-customer-bg px-4 py-10">
      <div className="customer-fade-in w-full max-w-[400px]">
        <div className="mb-8 flex flex-col items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-customer-primary ring-2 ring-customer-primary/30">
            <FontAwesomeIcon icon={faBuilding} className="h-7 w-7 text-customer-action-ink" />
          </div>
          <div className="text-center">
            <h1 className="text-xl font-semibold text-customer-text">Service Portal</h1>
            <p className="mt-0.5 text-xs font-semibold tracking-widest text-customer-primary-text uppercase">
              Customer Portal
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-customer-border bg-customer-surface p-8 shadow-sm">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-customer-text">{heading[step].title}</h2>
            <p className="mt-0.5 text-sm text-customer-text-secondary">{heading[step].blurb}</p>
            {/* D-143 — same component, same semantics as the staff plane; only the
                palette differs. `--customer-danger` is defined by all six skins in
                both light and dark, so the RED state travels with the theme. */}
            {step === 'verify' && (
              <RecoverySessionCounter
                id="recovery-session-counter-help"
                used={sessionRequestsUsed}
                limit={sessionRequestLimit}
                className="mt-3 text-xs"
                mutedClassName="text-customer-text-secondary"
                dangerClassName="rounded-lg border border-customer-danger/40 bg-customer-danger/10 p-3 text-customer-danger-text"
              />
            )}
          </div>

          {formError && (
            <div
              role="alert"
              className="customer-modal-in mb-5 rounded-lg border border-customer-warning/40 bg-customer-warning/10 px-3 py-2 text-sm text-customer-text"
            >
              {formError}
            </div>
          )}
          {notice && !formError && (
            <div
              role="status"
              className="customer-modal-in mb-5 rounded-lg border border-customer-primary/40 bg-customer-primary/10 px-3 py-2 text-sm text-customer-text"
            >
              {notice}
            </div>
          )}

          {/* ── Stage 1 ─────────────────────────────────────────────────── */}
          {step === 'request' && (
            <form onSubmit={submitRequest} noValidate className="space-y-5">
              <div className="space-y-1.5">
                <label htmlFor="activate-email" className="text-sm font-medium text-customer-text">
                  Email address
                  <span className="ml-0.5 text-customer-danger-text" aria-hidden="true">
                    *
                  </span>
                </label>
                <input
                  id="activate-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  autoComplete="email"
                  autoFocus
                  disabled={loading}
                  aria-invalid={!!fieldErrors.email}
                  aria-describedby={fieldErrors.email ? 'activate-email-error' : undefined}
                  className={inputClass}
                />
                {fieldErrors.email && (
                  <p
                    id="activate-email-error"
                    role="alert"
                    className="text-xs text-customer-danger-text"
                  >
                    {fieldErrors.email}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                aria-disabled={loading}
                className={buttonClass}
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="mr-2 h-4 w-4 animate-spin" />
                    Sending code...
                  </>
                ) : (
                  'Send me a code'
                )}
              </button>
            </form>
          )}

          {/* ── Stage 2 ─────────────────────────────────────────────────── */}
          {step === 'verify' && (
            <form onSubmit={submitVerify} noValidate className="space-y-5">
              <div className="space-y-1.5">
                <label htmlFor="activate-otp" className="text-sm font-medium text-customer-text">
                  6-digit code
                  <span className="ml-0.5 text-customer-danger-text" aria-hidden="true">
                    *
                  </span>
                </label>
                <input
                  id="activate-otp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  autoFocus
                  disabled={loading}
                  aria-invalid={!!fieldErrors.otp}
                  aria-describedby="activate-otp-hint"
                  className={`${inputClass} text-center font-mono text-lg tracking-[0.5em]`}
                />
                {fieldErrors.otp && (
                  <p role="alert" className="text-xs text-customer-danger-text">
                    {fieldErrors.otp}
                  </p>
                )}
                <p id="activate-otp-hint" className="text-xs text-customer-text-secondary">
                  {expiresAt
                    ? `This code expires at ${expiresAt.toLocaleTimeString()}.`
                    : 'This code expires shortly.'}{' '}
                  Never share it with anyone — nobody from your service provider will ask you for
                  it.
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                aria-disabled={loading}
                className={buttonClass}
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify code'
                )}
              </button>

              <div className="flex items-center justify-between text-xs">
                <button
                  type="button"
                  onClick={() => {
                    reset();
                    setNotice(null);
                    setOtp('');
                    setStep('request');
                  }}
                  disabled={loading}
                  className="flex items-center gap-1.5 text-customer-text-secondary hover:text-customer-text disabled:opacity-50"
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="h-3 w-3" />
                  Use a different email
                </button>
                <button
                  type="button"
                  onClick={() => void resend()}
                  // D-143 rule 4 — UX state only. The server still owns the account
                  // limit; this merely stops a spent browser from firing requests it
                  // can already see are pointless.
                  disabled={loading || cooldown > 0 || sessionRequestsUsed >= sessionRequestLimit}
                  aria-describedby="recovery-session-counter-help"
                  className="font-medium text-customer-primary-text hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
                >
                  {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
                </button>
              </div>
            </form>
          )}

          {/* ── Stage 3 ─────────────────────────────────────────────────── */}
          {step === 'password' && (
            <form onSubmit={submitPassword} noValidate className="space-y-5">
              <div className="space-y-1.5">
                <label
                  htmlFor="activate-password"
                  className="text-sm font-medium text-customer-text"
                >
                  New password
                  <span className="ml-0.5 text-customer-danger-text" aria-hidden="true">
                    *
                  </span>
                </label>
                <div className="relative">
                  <input
                    id="activate-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="new-password"
                    autoFocus
                    disabled={loading}
                    aria-invalid={!!fieldErrors.password}
                    aria-describedby="activate-password-rules"
                    className={`${inputClass} pr-11`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-customer-text-secondary hover:text-customer-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring focus-visible:ring-inset"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} className="h-4 w-4" />
                  </button>
                </div>
                <ul id="activate-password-rules" className="mt-2 space-y-1">
                  {PASSWORD_RULES.map((rule) => {
                    const met = rule.test(password);
                    return (
                      <li
                        key={rule.label}
                        className={`flex items-center gap-1.5 text-xs ${
                          met ? 'text-customer-primary-text' : 'text-customer-text-secondary'
                        }`}
                      >
                        <FontAwesomeIcon
                          icon={faCircleCheck}
                          className="h-3 w-3"
                          aria-hidden="true"
                        />
                        <span>{rule.label}</span>
                        <span className="sr-only">{met ? '— met' : '— not yet met'}</span>
                      </li>
                    );
                  })}
                </ul>
                {fieldErrors.password && (
                  <p role="alert" className="text-xs text-customer-danger-text">
                    {fieldErrors.password}
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <label
                  htmlFor="activate-confirm"
                  className="text-sm font-medium text-customer-text"
                >
                  Confirm password
                  <span className="ml-0.5 text-customer-danger-text" aria-hidden="true">
                    *
                  </span>
                </label>
                <input
                  id="activate-confirm"
                  type={showPassword ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="new-password"
                  disabled={loading}
                  aria-invalid={!!fieldErrors.confirm}
                  aria-describedby={fieldErrors.confirm ? 'activate-confirm-error' : undefined}
                  className={inputClass}
                />
                {fieldErrors.confirm && (
                  <p
                    id="activate-confirm-error"
                    role="alert"
                    className="text-xs text-customer-danger-text"
                  >
                    {fieldErrors.confirm}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                aria-disabled={loading}
                className={buttonClass}
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="mr-2 h-4 w-4 animate-spin" />
                    Setting password...
                  </>
                ) : (
                  'Set password'
                )}
              </button>
            </form>
          )}

          {/* ── Done ────────────────────────────────────────────────────── */}
          {step === 'done' && (
            <div className="space-y-5">
              <div className="flex flex-col items-center gap-3 py-2 text-center">
                <FontAwesomeIcon
                  icon={faShieldHalved}
                  className="h-10 w-10 text-customer-primary-text"
                  aria-hidden="true"
                />
                <p className="text-sm text-customer-text-secondary">
                  Your account is ready. Sign in with your email address and the password you just
                  created.
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate(CUSTOMER_PORTAL_ROUTES.LOGIN, { replace: true })}
                className={buttonClass}
              >
                Continue to sign in
              </button>
            </div>
          )}
        </div>

        {step !== 'done' && (
          <p className="mt-6 text-center text-xs text-customer-text-secondary">
            Already have a password?{' '}
            <Link
              to={CUSTOMER_PORTAL_ROUTES.LOGIN}
              className="font-medium text-customer-primary-text hover:underline"
            >
              Sign in
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}
